/**
 * Viability home — pick the question you're answering today.
 * Two cards: "Can we deliver?" (viability) and "When can we launch?"
 * (launch planning — appears only when time-phased planning is enabled;
 * absence, not disablement).
 */

import { useNavigate } from 'react-router-dom';
import { CalendarClock, ChevronRight, Scale } from 'lucide-react';
import { useTimePhasedPlanningEnabled } from '@/features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, StatusChip } from '../components/primitives';
import { useViabilityRun } from './useViabilityData';

export default function ViabilityHomeScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('viability')!;
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  const { scenario, scenariosLoaded, output } = useViabilityRun();

  const exec = output?.executiveSummary ?? null;

  return (
    <AppShell app={app}>
      <ScreenHeading
        title="Viability"
        subtitle={
          scenario
            ? `Working from scenario “${scenario.name}”.`
            : scenariosLoaded
              ? 'No scenario yet — the check runs on your live items with sensible defaults.'
              : 'Loading your planning setup…'
        }
      />
      <BallLine ballWith="you — pick the question" />
      <div className="space-y-3">
        <AppCard onClick={() => navigate('/apps/viability/check')}>
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-alpine-green-100 text-alpine-green-700 dark:bg-alpine-green-900/30 dark:text-alpine-green-300">
              <Scale className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                Can we deliver our priorities?
              </p>
              <p className="mt-0.5 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                The viability check: your priorities against your capacity, with a
                plain answer for each one.
              </p>
              {exec ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <StatusChip label={`${exec.includedCount} fit`} tone="green" />
                  {exec.deferredCount > 0 ? (
                    <StatusChip label={`${exec.deferredCount} don't fit`} tone="coral" />
                  ) : null}
                  {exec.needsSizingCount > 0 ? (
                    <StatusChip label={`${exec.needsSizingCount} need sizing`} tone="mustard" />
                  ) : null}
                </div>
              ) : null}
            </div>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400" />
          </div>
        </AppCard>

        {timePhasedEnabled ? (
          <AppCard onClick={() => navigate('/apps/viability/launch')}>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/30 dark:text-electric-mustard-300">
                <CalendarClock className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                  When can we launch?
                </p>
                <p className="mt-0.5 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                  Launch planning: three more steps — who's committed, the weekly
                  supply, and the money — then the calendar answers.
                </p>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400" />
            </div>
          </AppCard>
        ) : null}
      </div>
    </AppShell>
  );
}
